import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom-v5-compat';

import {
  OpDataFetchTable,
  OpDataFetchTableColumn,
} from 'new-components/DLS/OpDataFetchTable/OpDataFetchTable';
import { ApiAddButton } from 'new-components/DLS/OpTableCore/components/ApiAddButton';
import { ApiDeleteButton } from 'new-components/DLS/OpTableCore/components/ApiDeleteButton';
import { ApiEditButton } from 'new-components/DLS/OpTableCore/components/ApiEditButton';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { useIsMailroomEnabled } from 'utils/customHooks/useIsMailroomEnabled';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { opTime } from 'utils/dates';

export const RolesTable = ({
  orgId,
  uiStateKey,
  defaultFilters,
  gtm,
}: {
  orgId: number;
  uiStateKey: Utils.ValueOf<typeof tableStateKeys>;
  defaultFilters?: { name: string[] };
  gtm?: string;
}) => {
  const { t } = useTranslation();
  const [_, setSearchParams] = useSearchParams();

  const { hasWritePermission: canCreateRole } =
    useEndpointPermissions('createRole');
  const { hasWritePermission: canUpdateRole } =
    useEndpointPermissions('updateRole');
  const { hasWritePermission: canDeleteRole } =
    useEndpointPermissions('deleteRole');

  const deleteRole = useOpMutation({
    apiEndpointName: 'deleteRole',
    queryKeysToInvalidate: [['describeRole'], ['listRoles']],
  });

  const columns: OpDataFetchTableColumn<'listRoles'>[] = [
    {
      label: t('Id'),
      dataIndex: ['id'],
      sorter: true,
      filter: { type: 'input' },
      hidden: true,
      width: 150,
    },
    {
      label: t('Role name'),
      dataIndex: ['name'],
      ellipsis: true,
      sorter: true,
      filter: { type: 'input' },
      defaultFilteredValue: defaultFilters?.name,
    },
    {
      label: t('Description'),
      dataIndex: ['description'],
      ellipsis: true,
      sorter: true,
      filter: { type: 'input' },
    },
    {
      label: t('User count'),
      dataIndex: ['userCount'],
      width: 150,
      sorter: true,
    },
  ];

  const handleAdd = useCallback(() => {
    setSearchParams({ drawer: 'create' });
  }, [setSearchParams]);

  const handleEdit = useCallback(
    ({ id: editRoleId }: Api.Response['describeRole']) => {
      setSearchParams({ roleId: String(editRoleId), drawer: 'edit' });
    },
    [setSearchParams],
  );

  const handleDelete = useCallback(
    ({ id: deleteRoleId }: Api.Response['describeRole']) => {
      deleteRole.mutate({
        apiEndpointRequirements: [orgId, deleteRoleId],
      });
    },
    [deleteRole, orgId],
  );

  const isMailroomEnabled = useIsMailroomEnabled({ orgId });

  let preFilterText = '';
  if (!isMailroomEnabled) {
    preFilterText = 'name:(!~mailroom)';
  }

  const rolesPreFilter =
    preFilterText.length > 0 ? { preFilter: preFilterText } : {};

  return (
    <OpDataFetchTable
      uiStateKey={uiStateKey}
      height="calc(100vh - 344px)"
      gtm={gtm ? `${gtm}-table` : 'roles-table'}
      opQueryProps={{
        apiEndpointName: 'listRoles',
        parameters: [orgId, rolesPreFilter],
      }}
      columns={columns}
      allowExport={{ filename: `roles_${opTime()}` }}
      addButton={
        <ApiAddButton
          onClick={handleAdd}
          hasWritePermission={canCreateRole}
          itemName={t('role')}
        />
      }
      rowActions={(record) => [
        <ApiEditButton
          key="edit"
          onClick={() => handleEdit(record)}
          hasWritePermission={canUpdateRole}
        />,
        <ApiDeleteButton
          key="delete"
          onClick={() => handleDelete(record)}
          hasWritePermission={canDeleteRole}
        />,
      ]}
      onRowClick={handleEdit}
    />
  );
};
